import { createAsyncThunk } from "@reduxjs/toolkit";
import { baseUrl, headersDefault } from "./index";

const safeJson = async (res) => {
  try {
    return await res.json();
  } catch {
    return null;
  }
};

const extractList = (json) => {
  const list = Array.isArray(json) ? json : json?.data;
  return Array.isArray(list) ? list : [];
};

export const listPushNotificationDevices = createAsyncThunk(
  "profilePushNotifications/listDevices",
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetch(
        `${baseUrl}/api/v1/push-notifications/devices/list`,
        { headers: headersDefault() }
      );
      const json = await safeJson(res);
      if (!res.ok) {
        return rejectWithValue(
          json?.message ?? "Errore caricamento dispositivi"
        );
      }
      return { devices: extractList(json), meta: json?.meta ?? null };
    } catch (e) {
      return rejectWithValue(e?.message ?? "Errore rete");
    }
  }
);

// Inflight dedup for device creation — keyed by provider_device_id
let createDeviceInflight = {};

export const createPushNotificationDevice = createAsyncThunk(
  "profilePushNotifications/createDevice",
  async ({ providerDeviceId }, { rejectWithValue }) => {
    // If a request for the same device ID is already in-flight, wait for it
    if (createDeviceInflight[providerDeviceId]) {
      try {
        return await createDeviceInflight[providerDeviceId];
      } catch {
        return { created: false, device: null };
      }
    }

    const promise = (async () => {
      try {
        const res = await fetch(`${baseUrl}/api/v1/push-notifications/devices`, {
          method: "POST",
          headers: headersDefault(),
          body: JSON.stringify({ provider_device_id: providerDeviceId }),
        });
        const json = await safeJson(res);
        if (!res.ok) {
          // 409 (conflict) or 422 (unique validation) both mean the FCM token
          // is already registered (device reused across accounts / reinstall).
          // That's benign — treat as OK and let the caller refetch instead of
          // surfacing "Errore di registrazione dispositivo" to the user.
          if (res.status === 409 || res.status === 422)
            return { created: false, device: null };
          return rejectWithValue(
            json?.message ?? "Errore registrazione dispositivo"
          );
        }
        return { created: true, device: json?.data ?? json ?? null };
      } catch (e) {
        return rejectWithValue(e?.message ?? "Errore rete");
      }
    })();

    createDeviceInflight[providerDeviceId] = promise;
    try {
      return await promise;
    } finally {
      delete createDeviceInflight[providerDeviceId];
    }
  }
);

export const deletePushNotificationDevice = createAsyncThunk(
  "profilePushNotifications/deleteDevice",
  async ({ deviceId }, { rejectWithValue }) => {
    try {
      const res = await fetch(
        `${baseUrl}/api/v1/push-notifications/devices/${deviceId}`,
        { method: "DELETE", headers: headersDefault() }
      );
      if (!res.ok) {
        const json = await safeJson(res);
        return rejectWithValue(
          json?.message ?? "Errore rimozione dispositivo"
        );
      }
      return { deviceId };
    } catch (e) {
      return rejectWithValue(e?.message ?? "Errore rete");
    }
  }
);

export const listPushNotificationTopics = createAsyncThunk(
  "profilePushNotifications/listTopics",
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetch(
        `${baseUrl}/api/v1/push-notifications/topics/list/?visibility=visible&subscription=all&scope=enabled&page=1&limit=100`,
        { headers: headersDefault() }
      );
      const json = await safeJson(res);
      if (!res.ok) {
        return rejectWithValue(json?.message ?? "Errore caricamento notifiche");
      }
      return { topics: extractList(json), meta: json?.meta ?? null };
    } catch (e) {
      return rejectWithValue(e?.message ?? "Errore rete");
    }
  }
);

/**
 * Full flow:
 * 1) list devices
 * 2) create device if providerDeviceId missing
 * 3) list devices again
 * 4) list topics
 */
let syncInflight = null;

export const syncPushNotificationDeviceAndTopics = createAsyncThunk(
  "profilePushNotifications/sync",
  async ({ providerDeviceId }, { dispatch, rejectWithValue }) => {
    if (!providerDeviceId) {
      return rejectWithValue("Token notifiche non disponibile");
    }

    // Dedup: if a sync is already in-flight, piggyback on it
    if (syncInflight) {
      try {
        return await syncInflight;
      } catch {
        // previous one failed, proceed with a fresh call
      }
    }

    const doSync = async () => {
      try {
        // 1) list devices
        const r1 = await dispatch(listPushNotificationDevices());
        if (r1.error) return rejectWithValue(r1.error.message);
        const devices1 = r1.payload?.devices ?? [];

        const hasDevice = devices1.some(
          (d) =>
            String(d?.attributes?.provider_device_id ?? "") ===
            String(providerDeviceId)
        );

        // 2) create if missing
        let isNewDevice = false;
        if (!hasDevice) {
          const r2 = await dispatch(
            createPushNotificationDevice({ providerDeviceId })
          );
          // ignore 409; any other error bubbles
          if (r2.error && r2.payload) return rejectWithValue(r2.payload);
          isNewDevice = !r2.error;
        }

        // 3) list again
        const r3 = await dispatch(listPushNotificationDevices());
        if (r3.error) return rejectWithValue(r3.error.message);
        const devices = r3.payload?.devices ?? [];
        const deviceIds = devices
          .map((d) => Number(d?.id))
          .filter((x) => Number.isFinite(x));

        const currentDevice = devices.find(
          (d) =>
            String(d?.attributes?.provider_device_id ?? "") ===
            String(providerDeviceId)
        );

        // 4) auto-subscribe new devices to all topics
        if (isNewDevice && deviceIds.length > 0) {
          await dispatch(subscribeToAllPushNotificationTopics({ deviceIds }));
        }

        // 5) topics
        const r4 = await dispatch(listPushNotificationTopics());
        if (r4.error) return rejectWithValue(r4.error.message);
        const topics = r4.payload?.topics ?? [];

        return {
          providerDeviceId,
          devices,
          deviceIds,
          currentDeviceId: currentDevice ? Number(currentDevice.id) : null,
          topics,
        };
      } catch (e) {
        return rejectWithValue(e?.message ?? "Errore rete");
      }
    };

    syncInflight = doSync();
    try {
      return await syncInflight;
    } finally {
      syncInflight = null;
    }
  }
);

export const subscribeToPushNotificationTopic = createAsyncThunk(
  "profilePushNotifications/subscribeTopic",
  async ({ topicId, deviceIds }, { rejectWithValue }) => {
    try {
      const res = await fetch(
        `${baseUrl}/api/v1/push-notifications/topic-subscriptions/${topicId}/subscribe`,
        {
          method: "PUT",
          headers: headersDefault(),
          body: JSON.stringify({ devices: deviceIds }),
        }
      );
      const json = await safeJson(res);
      if (!res.ok) {
        return rejectWithValue(json?.message ?? "Errore iscrizione notifiche");
      }
      return { topicId: Number(topicId), subscribed: true };
    } catch (e) {
      return rejectWithValue(e?.message ?? "Errore rete");
    }
  }
);

export const subscribeToAllPushNotificationTopics = createAsyncThunk(
  "profilePushNotifications/subscribeAllTopics",
  async ({ deviceIds }, { rejectWithValue }) => {
    try {
      const res = await fetch(
        `${baseUrl}/api/v1/push-notifications/topic-subscriptions/all/subscribe`,
        {
          method: "PUT",
          headers: headersDefault(),
          body: JSON.stringify({ devices: deviceIds }),
        }
      );
      const json = await safeJson(res);
      if (!res.ok) {
        return rejectWithValue(
          json?.message ?? "Errore iscrizione a tutti i topic"
        );
      }
      return { subscribed: true };
    } catch (e) {
      return rejectWithValue(e?.message ?? "Errore rete");
    }
  }
);

export const unsubscribeFromPushNotificationTopic = createAsyncThunk(
  "profilePushNotifications/unsubscribeTopic",
  async ({ topicId, subscriptionId, deviceIds }, { rejectWithValue }) => {
    try {
      const res = await fetch(
        `${baseUrl}/api/v1/push-notifications/topic-subscriptions/${subscriptionId}/unsubscribe`,
        {
          method: "PUT",
          headers: headersDefault(),
          body: JSON.stringify({ devices: deviceIds }),
        }
      );
      const json = await safeJson(res);
      if (!res.ok) {
        return rejectWithValue(
          json?.message ?? "Errore disiscrizione notifiche"
        );
      }
      return { topicId: Number(topicId), subscribed: false };
    } catch (e) {
      return rejectWithValue(e?.message ?? "Errore rete");
    }
  }
);

export const unsubscribeFromAllPushNotificationTopics = createAsyncThunk(
  "profilePushNotifications/unsubscribeAllTopics",
  async ({ deviceIds }, { rejectWithValue }) => {
    try {
      const res = await fetch(
        `${baseUrl}/api/v1/push-notifications/topic-subscriptions/all/unsubscribe`,
        {
          method: "PUT",
          headers: headersDefault(),
          body: JSON.stringify({ devices: deviceIds }),
        }
      );
      const json = await safeJson(res);
      if (!res.ok) {
        return rejectWithValue(
          json?.message ?? "Errore disiscrizione da tutti i topic"
        );
      }
      return { subscribed: false };
    } catch (e) {
      return rejectWithValue(e?.message ?? "Errore rete");
    }
  }
);
